import { ConnectHeader } from '@/pages/company/common/ConnectHeader';
import { Modal as ModalContainer, Box, Grid, useTheme } from '@mui/material';
import { ConfirmationBox } from '@/pages/company/componants/Base';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useId, useState } from 'react';
import { ExclamationMarkIcon } from '@/icons/ExclamationMarkIcon';
import { TwoInputConnection } from '@/pages/company/common/TwoInputConnection';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { parse } from 'path';
import { useErrorHandling } from '@/utils/errorHandling';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { usePOSProvider } from '@/utils/providers';
import { TipsHistory } from '@/services/PaymentsServiceV2';
import { DATE_FORMAT } from '@/lib/constants';
import { useFormatting } from '@/utils/formatting';
import { SMMedium, SMSemiBold, SubTitle } from '@/typography/Typography';
import { UserContext } from '@/contexts/user/UserContext';
import { InputText } from '@/components/shared/InputText';
import {
    PrimaryButtonLG,
    SecondaryButtonGrayLG
} from '@/components/shared/Button';
import { isValidNumber } from '@/utils/validationHelpers';
import { useHandleResponse } from '@/lib/hooks/useHandleResponse';

interface EditTipsDialogProp {
    editTip: TipsHistory | null;
    onClose?: () => void;
}

export const EditTipsDialog = ({ editTip, onClose }: EditTipsDialogProp) => {
    const { t } = useTranslation();

    const { updateJournalAmount } = usePaymentsMutations();

    const [newAmount, setNewAmount] = useState<number | undefined>();

    const onCloseHandler = () => {
        setNewAmount(undefined);
        updateJournalAmount.reset();
        onClose?.();
    };

    const { getBalanceLabel } = useFormatting();
    const { company } = useContext(UserContext);

    useHandleResponse({
        error: null, // handled outside
        isSuccess: updateJournalAmount.isSuccess,
        successAlertMessage: t('tips.redistribution_successful'),
        isSuccessCallBack: () => {
            onCloseHandler();
        }
    });

    const theme = useTheme();

    return (
        <ModalContainer open={!!editTip} onClose={onCloseHandler}>
            <ConfirmationBox style={{ width: '450px' }}>
                <ConnectHeader
                    // logo={getLogoForPOSProvider()}
                    title={t('tips.edit_tips_for')}
                    description={t('tips.edit_tips_for_description', {
                        date: editTip?.date?.format(
                            DATE_FORMAT.DAY_NAME_MONTH_NAME_DAY_YEAR
                        ),
                        locationName: editTip?.locationName
                    })}
                />

                <SMMedium style={{ textAlign: 'center' }} mb={3} mt={2}>
                    {t('tips.current_tips_amount', {
                        amount: getBalanceLabel(
                            editTip?.totalTips,
                            company?.currency
                        )
                    })}
                </SMMedium>
                <InputText
                    error={updateJournalAmount.isError}
                    value={newAmount}
                    onChange={(e) => {
                        if (isValidNumber(e.target.value)) {
                            setNewAmount(parseInt(e.target.value as string));
                        } else {
                            setNewAmount(0);
                        }
                    }}
                    label={t('tips.new_tips_amount')}
                />
                {updateJournalAmount.error ? (
                    <SubTitle color={'error'}>
                        {t('tips.error_cant_distribute')}
                    </SubTitle>
                ) : (
                    <SubTitle>{t('tips.edit_pay')}</SubTitle>
                )}
                <Box
                    m={'20px 0'}
                    display={'flex'}
                    justifyContent={'space-between'}
                >
                    <SecondaryButtonGrayLG
                        onClick={onCloseHandler}
                        sx={{ width: '48%' }}
                    >
                        <SMSemiBold>{t('cancel')}</SMSemiBold>
                    </SecondaryButtonGrayLG>

                    <PrimaryButtonLG
                        loading={updateJournalAmount.isPending}
                        disabled={
                            !newAmount ||
                            newAmount < 0 ||
                            updateJournalAmount.isError
                        }
                        onClick={() => {
                            updateJournalAmount.mutate({
                                posJournalId: editTip?.posJournalId as string,
                                newTotalAmount: newAmount!.toString()
                            });
                        }}
                        sx={{ width: '48%' }}
                    >
                        <SMSemiBold>{t('tips.redistribute')}</SMSemiBold>
                    </PrimaryButtonLG>
                </Box>
            </ConfirmationBox>
        </ModalContainer>
    );
};
